import { Component, OnDestroy, OnInit, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { CreateTrademarkFamilyDialogComponent } from 'src/app/dialogs/create-trademark-family-dialog/create-trademark-family-dialog.component';
import { NiceClass } from 'src/app/models/niceClass';
import { Territory } from 'src/app/models/territory';
import { Trademark } from 'src/app/models/trademark';
import { TrademarkFamily } from 'src/app/models/trademarkFamily';
import { MessageService } from 'src/app/services/message.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { TrademarkFamilyCreate, TrademarkFamilyService } from 'src/app/services/trademark-family.service';
import { TrademarkService } from 'src/app/services/trademark.service';
import { TerritoryService } from '../../../services/territory.service';
import {
  QueryParamManager,
  optionalString,
  numberArray,
  stringArray,
} from 'src/app/helpers/query-params.helper';
import { synchronizationFilterOptions } from 'src/app/helpers/trademark-synchronization.helper';
import { Option } from 'src/app/components/select/select.component';

type TrademarkFilters = {
  query?: string;
  classNumber: number[];
  countryOfDesignation: string[];
  families?: string[];
  synchronization: string[];
}

type TrademarkFamilyFilters = {
  query?: string;
}

@Component({
  selector: 'app-trademarks-list',
  templateUrl: './trademarks-list.component.html',
  styleUrls: ['./trademarks-list.component.scss']
})
export class TrademarksListComponent implements OnInit, OnDestroy {

  displayMode: string = "trademarks";

  loading: boolean = false;
  done: boolean = false;
  noDocs: boolean = false;

  displayedColumns: string[] = ['name', 'identifier', 'countryOfDesignation', 'niceClassification', 'status', 'representation', 'actions'];

  readonly synchronizationOptions: Option[] = synchronizationFilterOptions();

  dataSource: MatTableDataSource<Trademark> = new MatTableDataSource<Trademark>([]);
  dataSourceFamilies: MatTableDataSource<TrademarkFamily> = new MatTableDataSource<TrademarkFamily>([]);

  readonly tmManager = new QueryParamManager<TrademarkFilters, Trademark>(
    {
      defaultLimit: 10,
      defaultSort: 'name',
      fields: {
        query: {
          paramKey: 'query',
          deserialize: optionalString,
          serialize: (v) => v || undefined,
        },
        classNumber: {
          paramKey: 'classNumber',
          deserialize: numberArray,
          serialize: (v) => v.length > 0 ? v : undefined,
        },
        countryOfDesignation: {
          paramKey: 'countryOfDesignation',
          deserialize: stringArray,
          serialize: (v) => v.length > 0 ? v : undefined,
        },
        families: {
          paramKey: 'families',
          deserialize: stringArray,
          serialize: (v) => v && v.length > 0 ? v : undefined,
        },
        synchronization: {
          paramKey: 'synchronization',
          deserialize: stringArray,
          serialize: (v) => v.length > 0 ? v : undefined,
        },
      },
    },
    this.router,
  );

  readonly familyManager = new QueryParamManager<TrademarkFamilyFilters, TrademarkFamily>(
    {
      namespace: 'family',
      defaultLimit: 10,
      defaultSort: 'name',
      fields: {
        query: {
          paramKey: 'familyQuery',
          deserialize: optionalString,
          serialize: v => v || undefined,
        },
      },
    },
    this.router,
  );


  private readonly filtersSubject = new Subject<TrademarkFilters>();
  private filtersSubscription?: Subscription;

  private readonly filtersFamilySubject = new Subject<TrademarkFamilyFilters>();
  private filtersFamilySubscription?: Subscription;

  private readonly pagerSubject = new Subject<PageEvent>();
  private pagerSubscription?: Subscription;

  private readonly pagerFamilySubject = new Subject<PageEvent>();
  private pagerFamilySubscription?: Subscription;

  loadingFamilies: boolean = false;
  doneFamilies: boolean = false;
  noDocsFamilies: boolean = false;

  selectedFamilies = signal<TrademarkFamily[]>([]);

  private territoryById = new Map<string, Territory>();

  noTrademarkButton: {
    label: string;
    icon: string;
    class: string;
    action: Function
  } = {
    label: 'TRADEMARKS_LIST.NEW',
    icon: 'add_circle_outline',
    class: 'main-button',
    action: () => { this.router.navigate(['new'], { relativeTo: this.route }); },
  }

  noTrademarkFamiliesButton: {
    label: string;
    icon: string;
    class: string;
    action: Function
  } = {
    label: 'TRADEMARKS_LIST.NEW_FAMILY',
    icon: 'add_circle_outline',
    class: 'main-button',
    action: () => { this.newFamily(); },
  };

  constructor(
    private readonly _message: MessageService,
    private readonly dialog: MatDialog,
    private readonly organizationService: OrganizationService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly trademarkService: TrademarkService,
    private readonly trademarkFamilyService: TrademarkFamilyService,
    private readonly territoryService: TerritoryService,
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    this.loading = true;
    this.loadingFamilies = true;
    this.done = false;
    this.doneFamilies = false;

    this.filtersSubscription = this.filtersSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(async () => {
        this.tmManager.resetPage();
        this.applyAllQueryParams();
        await this.retrieveTrademarks(false);
      });

    this.filtersFamilySubscription = this.filtersFamilySubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(async () => {
        this.familyManager.resetPage();
        this.applyAllQueryParams();
        await this.retrieveTrademarkFamilies(false);
      });

    this.pagerSubscription = this.pagerSubject
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(async () => {
        await this.retrieveTrademarks(false);
      });

    this.pagerFamilySubscription = this.pagerFamilySubject
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(async () => {
        await this.retrieveTrademarkFamilies(false);
      });

    //read filters from query params, then initial load

    let isFirstLoad = true;
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (params) => {
        this.tmManager.fromParams(params);
        this.familyManager.fromParams(params);

        // build territory index once
        if (!this.territoryById.size) {
          this.territoryById = new Map(
            (await this.territoryService.retrieve()).map((t) => [t.id, t]),
          );
        }

        // resolve family IDs to objects for select component
        const familyIds = this.tmManager.filters.families;
        if (familyIds && familyIds.length > 0) {
          const currentIds = this.selectedFamilies().map(f => f._id);
          const changed = familyIds.length !== currentIds.length
            || familyIds.some(id => !currentIds.includes(id));
          if (changed) {
            try {
              this.selectedFamilies.set(
                await this.trademarkFamilyService.retrieveByIds(familyIds),
              );
            } catch {
              this.selectedFamilies.set([]);
            }
          }
        } else {
          this.selectedFamilies.set([]);
        }

        if (isFirstLoad) {
          isFirstLoad = false;
          await Promise.all([
            this.retrieveTrademarks(true),
            this.retrieveTrademarkFamilies(true),
          ]);
          this.loading = false;
          this.loadingFamilies = false;
          this.done = true;
          this.doneFamilies = true;
        }
      });
  }

  public ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.filtersFamilySubscription?.unsubscribe();
    this.pagerSubscription?.unsubscribe();
    this.pagerFamilySubscription?.unsubscribe();
  }

  // URL sync (combines both managers)
  private applyAllQueryParams(): Promise<boolean> {
    const params: Record<string, unknown> = {
      ...this.tmManager.toParams(),
      ...this.familyManager.toParams(),
    };

    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
    });
  }

  async onTabChange(event: any) {
    if (event.index === 0) {
      this.displayMode = "trademarks";
      await this.retrieveTrademarks(false);
    } else if (event.index === 1) {
      this.displayMode = "families";
      await this.retrieveTrademarkFamilies(false);
    }
  }

  async retrieveTrademarks(init: boolean) {
    try {
      this._message.emitChange('LOADING', 'START');

      const p = this.tmManager.pager;
      const result = await this.trademarkService.retrieveAll(
        p.page,
        p.limit,
        p.offset,
        p.sort,
        this.getExpandedCountryCodes(),
        this.tmManager.filters.classNumber,
        this.tmManager.filters.query,
        this.tmManager.filters.families,
        this.tmManager.filters.synchronization,
      );
      this.tmManager.pager = { ...p, ...result };
      this.dataSource = new MatTableDataSource<Trademark>(this.tmManager.pager.docs);

      if (init && this.tmManager.pager.totalDocs === 0) {
        this.noDocs = true;
      } else if (init && this.tmManager.pager.totalDocs > 0) {
        this.noDocs = false;
      }

      this._message.emitChange('LOADING', 'END');
    } catch (err) {
      console.error(err);
      this._message.emitChange('LOADING', 'END');
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  async retrieveTrademarkFamilies(init: boolean) {
    try {
      this._message.emitChange('LOADING', 'START');

      const p = this.familyManager.pager;
      const result = await this.trademarkFamilyService.retrieveAll(
        p.page,
        p.limit,
        p.offset,
        p.sort,
        this.familyManager.filters.query,
      );
      this.familyManager.pager = { ...p, ...result };
      this.dataSourceFamilies = new MatTableDataSource<TrademarkFamily>(this.familyManager.pager.docs);

      if (init && this.familyManager.pager.totalDocs === 0) {
        this.noDocsFamilies = true;
      } else if (init && this.familyManager.pager.totalDocs > 0) {
        this.noDocsFamilies = false;
      }

      this._message.emitChange('LOADING', 'END');
    } catch (err) {
      console.error(err);
      this._message.emitChange('LOADING', 'END');
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  // paginator events
  async handlePage(event: PageEvent) {
    this.tmManager.handlePage(event);
    this.applyAllQueryParams();
    this.pagerSubject.next(event);
  }

  async handleFamilyPage(event: PageEvent) {
    this.familyManager.handlePage(event);
    this.applyAllQueryParams();
    this.pagerFamilySubject.next(event);
  }

  onSortChange(event: any) {
    this.tmManager.onSortChange(event);
    this.applyAllQueryParams();
    this.retrieveTrademarks(false);
  }

  onSortFamilyChange(event: any) {
    this.familyManager.onSortChange(event);
    this.applyAllQueryParams();
    this.retrieveTrademarkFamilies(false);
  }

  // filter change handlers
  onTrademarkFamilyChange(event: any) {
    this.tmManager.filters.families = event.value.map((family: TrademarkFamily) => family._id);
    this.selectedFamilies.set(event.value);
    this.applyAllQueryParams();
    this.filtersSubject.next({ ...this.tmManager.filters });
  }

  onNiceClassificationChange(event: any) {
    this.tmManager.filters.classNumber = event.value.map((niceClass: NiceClass) => niceClass.classNumber);
    this.applyAllQueryParams();
    this.filtersSubject.next({ ...this.tmManager.filters });
  }

  onCountryOfDesignationChange(event: any) {
    this.tmManager.filters.countryOfDesignation = event.value.map((t: Territory) => t.id);
    this.applyAllQueryParams();
    this.filtersSubject.next({ ...this.tmManager.filters });
  }

  onSynchronizationChange(event: any) {
    this.tmManager.filters.synchronization = event.value;
    this.applyAllQueryParams();
    this.filtersSubject.next({ ...this.tmManager.filters });
  }

  onQueryChange(query: any) {
    if (typeof query === 'string') {
      this.tmManager.filters.query = query;
      this.applyAllQueryParams();
      this.filtersSubject.next({ ...this.tmManager.filters });
    }
  }

  onQueryFamilyChange(query: any) {
    if (typeof query === 'string') {
      this.familyManager.filters.query = query;
      this.applyAllQueryParams();
      this.filtersFamilySubject.next({ ...this.familyManager.filters });
    }
  }

  toTrademark(trademark: Trademark) {
    this.router.navigate([trademark._id], {
      relativeTo: this.route,
      state: {
        from: "TRADEMARKS"
      }
    })
  }

  formatDate(date: Moment) {
    return moment(date).format('ll');
  }

  // expand territory IDs to include alternate codes for API queries
  private getExpandedCountryCodes(): string[] {
    const ids = this.tmManager.filters.countryOfDesignation;
    if (!ids?.length || !this.territoryById.size) return ids || [];
    return ids.flatMap((id) => {
      const t = this.territoryById.get(id);
      if (!t) return [];
      return t.alternateCodes?.length
        ? [t.id, ...t.alternateCodes]
        : [t.id];
    });
  }

  async newFamily() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
      }
    }
    const dialog = this.dialog.open(CreateTrademarkFamilyDialogComponent, config);
    const result: { created: boolean, family?: Partial<TrademarkFamily> } = await firstValueFrom(dialog.afterClosed());
    if (result && result.created && result.family) {
      try {
        const toCreate: TrademarkFamilyCreate = {
          name: result.family.name,
          label: result.family.label,
          notes: result.family.notes,
          type: result.family.type,
          trademarks: []
        }
        await this.trademarkFamilyService.create(toCreate);
        this.toastr.success(this.translate.instant(`TRADEMARK_FAMILY.FAMILY_CREATED`, { name: toCreate.label }));
        await this.retrieveTrademarkFamilies(true);
      } catch (err) {
        this.toastr.error(`ERRORS.GENERIC`)
      }

    }
  }

}
